import { useState, useEffect, useRef } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { activityPath, primaryCategory, orderedActivityCategories } from '@/lib/activityUrl';
import { container, containerNarrow, sectionBgLight, sectionBgWhite } from '@/lib/ds';
import { base44 } from '@/api/base44Client';
import { useLanguage } from '@/components/utils/useLanguage';
import { useFavorites } from '@/components/utils/useFavorites';
import { useAnalytics } from '@/components/utils/useAnalytics';
import { useOpeningHours } from '@/components/utils/useOpeningHours';
import ReviewSection from '@/components/ui/ReviewSection';
import FavoriteHintCard from '@/components/ui/FavoriteHintCard';
import ActivityStickyNav from '@/components/ui/ActivityStickyNav';
import TourBuilder from '@/components/planner/TourBuilder';
import AddToTourDialog from '@/components/ui/AddToTourDialog';
import { Card, CardContent } from '@/components/ui/card';

import Head from '@/components/ui/Head';
import JsonLd from '@/components/ui/JsonLd';
import { buildActivityJsonLd } from '@/lib/jsonld';
import { burstFavoriteConfetti } from '@/lib/confetti';

import ActivityDetailSkeleton from '@/components/activity/detail/ActivityDetailSkeleton';
import ActivityNotFound from '@/components/activity/detail/ActivityNotFound';
import ActivityMobileBar from '@/components/activity/detail/ActivityMobileBar';
import ActivityDetailHeader from '@/components/activity/detail/ActivityDetailHeader';
import ActivityPhotoStacks from '@/components/activity/detail/ActivityPhotoStacks';
import ActivityIntro from '@/components/activity/detail/ActivityIntro';
import ActivityKeyFacts from '@/components/activity/detail/ActivityKeyFacts';
import ActivityRichText from '@/components/activity/detail/ActivityRichText';
import ActivityFacilities from '@/components/activity/detail/ActivityFacilities';
import ActivityAdmissionPrices from '@/components/activity/detail/ActivityAdmissionPrices';
import ActivityDealsCard from '@/components/activity/detail/ActivityDealsCard';
import ActivityAffiliateCard from '@/components/activity/detail/ActivityAffiliateCard';
import ActivityDiscountCards from '@/components/activity/detail/ActivityDiscountCards';
import ActivityGettingThere from '@/components/activity/detail/ActivityGettingThere';
import ActivityMoreActivities from '@/components/activity/detail/ActivityMoreActivities';
import ActivityShareDialog from '@/components/activity/detail/ActivityShareDialog';

export default function ActivityDetail() {
  const { t, getField } = useLanguage();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { trackActivityView, trackAffiliateClick, trackFavoriteSave, trackActivityShare } = useAnalytics();
  const { checkOpenNow, getNextOpenInfo } = useOpeningHours();

  const [activity, setActivity] = useState(null);
  const [discountCards, setDiscountCards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [labels, setLabels] = useState([]);
  const [deals, setDeals] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [relatedActivities, setRelatedActivities] = useState([]);
  const [nearbyActivities, setNearbyActivities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [partner, setPartner] = useState(null);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tourActivities, setTourActivities] = useState([]);
  const [tourStarted, setTourStarted] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tourAddOpen, setTourAddOpen] = useState(false);
  const headerRef = useRef(null);
  const [stickyVisible, setStickyVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const el = headerRef.current;
      const trigger = el ? el.offsetTop + el.offsetHeight : 600;
      setStickyVisible(window.scrollY + 112 > trigger);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [activity]);

  // The slug comes from the canonical path (/<category-slug>/<activity-slug>) or,
  // for links that predate it, from the ?slug= query parameter.
  const { activitySlug } = useParams();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const slug = activitySlug || urlParams.get('slug');
  const id = urlParams.get('id');

  useEffect(() => {
    const fetchData = async () => {
      if (!slug && !id) return;

      // Record page view by slug via platform Event Tracking
      if (slug) {
        base44.analytics.track({ eventName: 'activity_detail_page_view', properties: { slug } });
      }

      try {
        const [acts, cats, lbls, dls, regs, seas, allCards, allPublished] = await Promise.all([
          id
            ? base44.entities.Activity.filter({ id })
            : base44.entities.Activity.filter({ slug, published: true }),
          base44.entities.Category.list(),
          base44.entities.Label.list(),
          base44.entities.Deal.filter({ is_active: true }),
          base44.entities.Region.list(),
          base44.entities.Season.list(),
          base44.entities.DiscountCard.filter({ is_active: true }),
          base44.entities.Activity.filter({ published: true }, '-popularity_score')
        ]);
        setDiscountCards(allCards);
        setSeasons(seas);
        setRegions(regs);

        if (acts.length > 0) {
          const act = acts[0];
          setActivity(act);
          setTourActivities([act]);

          // Track view
          trackActivityView(act.id, act.region_id, primaryCategory(act, cats)?.id || act.category_ids?.[0]);

          // Fetch reviews + partner in parallel
          const [revs, partnerResult, photoList] = await Promise.all([
            base44.entities.Review.filter({ activity_id: act.id, is_approved: true }),
            act.partner_id ? base44.entities.Partner.filter({ id: act.partner_id }) : Promise.resolve([]),
            base44.entities.ActivityPhoto.filter({ activity_id: act.id }, 'sort_order')
          ]);
          setReviews(revs);
          if (partnerResult.length > 0) setPartner(partnerResult[0]);
          setPhotos(photoList);

          // Fetch related activities - prioritize subcategory > parent category > region
          const allCats = cats; // already fetched above

          const others = allPublished.filter(r => r.id !== act.id);
          const byScore = (a, b) => (b.popularity_score || 0) - (a.popularity_score || 0);

          // Split act's categories into subcategories (has parent) and top-level
          const actSubCatIds = (act.category_ids || []).filter(cid => allCats.find(c => c.id === cid)?.parent_category_id);
          const actParentCatIds = (act.category_ids || []).filter(cid => !allCats.find(c => c.id === cid)?.parent_category_id);
          // Parent IDs of act's subcategories (for tier-2 matching)
          const actSubParentIds = actSubCatIds.map(cid => allCats.find(c => c.id === cid)?.parent_category_id).filter(Boolean);

          // 1. Same subcategory
          const sameSubCat = others
            .filter(r => r.category_ids?.some(cid => actSubCatIds.includes(cid)))
            .sort(byScore);

          const usedIds = new Set([act.id, ...sameSubCat.map(r => r.id)]);

          // 2. Same parent category
          const allParentIds = [...new Set([...actParentCatIds, ...actSubParentIds])];
          const sameParentCat = others
            .filter(r => !usedIds.has(r.id) && r.category_ids?.some(cid => allParentIds.includes(cid)))
            .sort(byScore);

          sameParentCat.forEach(r => usedIds.add(r.id));

          // 3. Same region fallback
          const regionFallback = others
            .filter(r => !usedIds.has(r.id) && r.region_id === act.region_id)
            .sort(byScore);

          setRelatedActivities([...sameSubCat, ...sameParentCat, ...regionFallback].slice(0, 10));

          // Nearby activities (within 5 km, sorted by popularity, max 6)
          if (act.address?.latitude && act.address?.longitude) {
            const toRad = deg => deg * Math.PI / 180;
            const haversine = (lat1, lon1, lat2, lon2) => {
              const R = 6371;
              const dLat = toRad(lat2 - lat1);
              const dLon = toRad(lon2 - lon1);
              const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
              return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            };
            const withDist = others
              .filter(r => r.address?.latitude && r.address?.longitude)
              .map(r => ({ ...r, _distanceKm: haversine(act.address.latitude, act.address.longitude, r.address.latitude, r.address.longitude) }))
              .filter(r => r._distanceKm <= 1)
              .sort((a, b) => a._distanceKm - b._distanceKm)
              .slice(0, 10);
            setNearbyActivities(withDist);
          }
        }

        setCategories(cats);
        setLabels(lbls);
        setDeals(dls);
      } catch (error) {
        console.error('Error fetching activity:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, id]);

  const handleFavoriteClick = (e) => {
    const wasAdded = !isFavorite(activity.id);
    toggleFavorite(activity.id);
    if (wasAdded) {
      trackFavoriteSave(activity.id);
      burstFavoriteConfetti(e.currentTarget);
    }
  };

  const handleShare = () => setShareOpen(true);

  const handleAffiliateClick = (link) => {
    trackAffiliateClick(activity.id, activity.partner_id);
    window.open(link.url, '_blank');
  };

  const handleToggleNearbyTour = (nearbyActivity) => {
    setTourStarted(true);
    setTourActivities(prev => {
      const isSelected = prev.find(a => a.id === nearbyActivity.id);
      if (isSelected) {
        return prev.filter(a => a.id !== nearbyActivity.id);
      } else {
        return [...prev, nearbyActivity];
      }
    });
  };

  if (loading) {
    return <ActivityDetailSkeleton />;
  }

  if (!activity) {
    return <ActivityNotFound />;
  }

  // Canonical URL for this activity: a path whose category segment doesn't match
  // the activity is swapped for the canonical one, once, keeping the rest of the
  // query string (e.g. campaign parameters) intact.
  const canonicalPath = activityPath(activity, categories);
  // Only act on the activity the URL actually asked for: while another one is
  // still loading, `activity` is the previously opened record.
  const isRequestedActivity = !slug || activity.slug === slug;
  if (canonicalPath && isRequestedActivity && location.pathname !== canonicalPath) {
    return (
      <Navigate
        to={{ pathname: canonicalPath, search: location.search, hash: location.hash }}
        replace
      />
    );
  }

  const activityCategories = orderedActivityCategories(activity, categories);

  const activityDeals = deals.filter(d => activity.deal_ids?.includes(d.id));
  const favorite = isFavorite(activity.id);

  const openStatus = checkOpenNow(activity.opening_hours, activity.always_open);
  // "Jetzt geöffnet" pill shown only if open and not closing within 1.5 hours
  const showOpenNowPill = openStatus.isOpen && openStatus.status !== 'closing_soon';
  const isFamilienGetestet = (activity.weitere_filter || []).includes('familien-getestet');

  const ogImage = activity.image || '';
  const metaTitle = getField(activity, 'meta_title') || getField(activity, 'title');
  const metaDesc = getField(activity, 'meta_description') || getField(activity, 'summary');

  const activityJsonLd = buildActivityJsonLd(activity, { categories, reviews, seasons, getField, t });

  return (
    <div className={`min-h-screen ${sectionBgLight}`}>
      <Head
        title={`${getField(activity, 'title')} · Ausflug für Familien | Basel mit Kindern`}
        meta={[
          ['property', 'og:image', ogImage],
          ['property', 'og:title', metaTitle],
          ['property', 'og:description', metaDesc],
        ]}
      />
      <JsonLd data={activityJsonLd} />

      <ActivityStickyNav
        activity={activity}
        favorite={favorite}
        onFavoriteClick={handleFavoriteClick}
        onShare={handleShare}
        onAddToTour={() => setTourAddOpen(true)}
        regions={regions}
        visible={stickyVisible}
      />

      {/* Mobile top bar - Controls + Badges */}
      <ActivityMobileBar
        activity={activity}
        favorite={favorite}
        onFavoriteClick={handleFavoriteClick}
        onShare={handleShare}
        onAddToTour={() => setTourAddOpen(true)}
        openStatus={openStatus}
        showOpenNowPill={showOpenNowPill}
        activityDeals={activityDeals}
      />

      {/* Content */}
      <ActivityDetailHeader
        activity={activity}
        headerRef={headerRef}
        activityCategories={activityCategories}
        categories={categories}
        regions={regions}
        favorite={favorite}
        onFavoriteClick={handleFavoriteClick}
        onShare={handleShare}
        onAddToTour={() => setTourAddOpen(true)}
        openStatus={openStatus}
        showOpenNowPill={showOpenNowPill}
      />

      <ActivityPhotoStacks
        activity={activity}
        photos={photos}
        activityDeals={activityDeals}
        isFamilienGetestet={isFamilienGetestet}
      />

      <ActivityIntro activity={activity} />

      {/* Content */}
      <section className={`${containerNarrow} py-2 sm:py-4 lg:py-8`}>
        <div className="space-y-12">
          <div>
            <ActivityKeyFacts
              activity={activity}
              seasons={seasons}
              openStatus={openStatus}
              showOpenNowPill={showOpenNowPill}
              getNextOpenInfo={getNextOpenInfo}
            />

            <ActivityRichText activity={activity} />

            {/* Kindergeburtstag highlight */}
            {activity.kindergeburtstag && (
              <div className="flex items-center gap-3 p-4 rounded-2xl bg-purple-100 my-8">
                <div>
                  <p className="font-bold text-purple-900 text-xl sm:text-2xl lg:text-3xl">{t('Perfekt für Kindergeburtstage', 'Perfect for birthday parties')}</p>
                  <p className="text-purple-900">{t('Diese Location eignet sich ideal für Geburtstagspartys mit Kindern.', 'This location is ideal for birthday parties with children.')}</p>
                </div>
              </div>
            )}

            <ActivityFacilities activity={activity} />

            <ActivityAdmissionPrices activity={activity} />

            <ActivityDealsCard deals={activityDeals} />

            <ActivityAffiliateCard activity={activity} onAffiliateClick={handleAffiliateClick} />

            <ActivityDiscountCards activity={activity} discountCards={discountCards} />

            <ActivityGettingThere activity={activity} />
          </div>

          {/* Partner Info */}
          {partner && (
            <Card className="border-0 bg-emerald-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {partner.logo && (
                    <img src={partner.logo} alt={partner.company_name} className="w-12 h-12 rounded-lg object-contain bg-white" />
                  )}
                  <div>
                    <p className="text-sm text-emerald-700">{t('Partner', 'Partner')}</p>
                    <p className="font-bold text-gray-900">{partner.company_name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews */}
          <div id="bewertungen" className="py-8">
            <ReviewSection
              activityId={activity.id}
              reviews={reviews}
              onReviewAdded={() => {
                base44.entities.Review.filter({ activity_id: activity.id, is_approved: true })
                  .then(setReviews);
              }}
            />
          </div>

          {/* Favorite hint card */}
          <FavoriteHintCard
            activity={activity}
            favorite={favorite}
            onFavorite={handleFavoriteClick}
            onShare={handleShare}
          />
        </div>
      </section>

      <ActivityMoreActivities
        activity={activity}
        relatedActivities={relatedActivities}
        nearbyActivities={nearbyActivities}
        tourActivities={tourActivities}
        onToggleNearbyTour={handleToggleNearbyTour}
        categories={categories}
        labels={labels}
        deals={deals}
      />

      {tourStarted && tourActivities.length >= 1 && (
        <TourBuilder
          selectedActivities={tourActivities}
          onRemove={(id) => setTourActivities(prev => prev.filter(a => a.id !== id))}
          onClear={() => { setTourActivities(activity ? [activity] : []); setTourStarted(false); }}
        />
      )}

      {/* Meta */}
      <section className={`${sectionBgWhite}`}>
        <div className={`${container} pb-8`}>
          {/* Last updated + Disclaimer */}
          <h1 className="text-sm text-gray-500">
            {t('Lohnt sich ', 'Is')}
            {getField(activity, 'title')}
            {t(` mit Kindern? Aktuell für ${new Date().getFullYear()} mit Fotos.`, ` worth a try with kids? Currently for ${new Date().getFullYear()} with pictures.`)}
          </h1>
          {/* Last updated + Disclaimer */}
          <div className="text-sm text-gray-500 space-y-1">
            {activity.updated_date && (
              <p>{t('Inhalt zuletzt aktualisiert am', 'Content last updated on')} {new Date(activity.updated_date).toLocaleDateString('de-CH')}. {t('Wir können die Richtigkeit der obigen Infos nicht garantieren. Bitte prüfe die Öffnungszeiten und Preise direkt auf der offiziellen Webseite des Anbieters.', 'We cannot guarantee the accuracy of the information above. Please check the opening hours and prices directly on the provider’s official website.')}</p>
            )}
          </div>
        </div>
      </section>

      {/* Share Modal */}
      <ActivityShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        activity={activity}
        onTrackShare={(channel) => trackActivityShare(activity.id, channel)}
      />

      {/* Add to Tour Modal */}
      <AddToTourDialog
        open={tourAddOpen}
        onOpenChange={setTourAddOpen}
        activity={activity}
      />
    </div>
  );
}